import React, { FC, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { ApiClientMode, ApiClientScope, CreateApiClientInput } from 'models/api-client';

type CreateApiClientDialogProps = {
    open: boolean;
    organizationId: string;
    organizationName: string;
    onCancel: () => void;
    onSubmit: (input: CreateApiClientInput) => void;
};

const CreateApiClientDialog: FC<CreateApiClientDialogProps> = ({
    open,
    organizationId,
    organizationName,
    onCancel,
    onSubmit,
}: CreateApiClientDialogProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const [name, setName] = useState('');
    const [mode, setMode] = useState<ApiClientMode>(ApiClientMode.TEST);
    const [scopes, setScopes] = useState<ApiClientScope[]>([ApiClientScope.orders_read]);

    const resetAndClose = () => {
        setName('');
        setMode(ApiClientMode.TEST);
        setScopes([ApiClientScope.orders_read]);
        onCancel();
    };

    const toggleScope = (scope: ApiClientScope, checked: boolean) => {
        setScopes((current) => (checked ? [...current, scope] : current.filter((s) => s !== scope)));
    };

    const handleSubmit = () => {
        onSubmit({
            organization: organizationId,
            name: name.trim(),
            mode,
            scopes,
        });
        resetAndClose();
    };

    const canSubmit = name.trim().length > 0 && scopes.length > 0;

    return (
        <DHDialog
            open={open}
            onClose={resetAndClose}
            maxWidth="sm"
            fullWidth
            title={translate('app-settings:CREATE_API_CLIENT')}
            actions={
                <Box display="flex" gap={1}>
                    <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
                        {translate('app-settings:CREATE_API_CLIENT')}
                    </Button>
                </Box>
            }
        >
            <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                    {translate('app-settings:CREATE_API_CLIENT_FOR_LAB', {
                        lab: organizationName,
                    })}
                </Typography>
            </Box>
            <Box mb={2}>
                <TextField
                    fullWidth
                    required
                    label={translate('app-settings:API_CLIENT_NAME')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Box>
            <Box mb={2}>
                <FormControl fullWidth>
                    <InputLabel>{translate('app-settings:API_CLIENT_MODE')}</InputLabel>
                    <Select
                        label={translate('app-settings:API_CLIENT_MODE')}
                        value={mode}
                        onChange={(e) => setMode(e.target.value as ApiClientMode)}
                    >
                        <MenuItem value={ApiClientMode.TEST}>{translate('app-settings:API_CLIENT_MODE_TEST')}</MenuItem>
                        <MenuItem value={ApiClientMode.PROD}>{translate('app-settings:API_CLIENT_MODE_PROD')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:API_CLIENT_SCOPES')}
                </Typography>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={scopes.includes(ApiClientScope.orders_read)}
                                onChange={(_, checked) => toggleScope(ApiClientScope.orders_read, checked)}
                            />
                        }
                        label={translate('app-settings:API_CLIENT_SCOPE_ORDERS_READ')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={scopes.includes(ApiClientScope.orders_write)}
                                onChange={(_, checked) => toggleScope(ApiClientScope.orders_write, checked)}
                            />
                        }
                        label={translate('app-settings:API_CLIENT_SCOPE_ORDERS_WRITE')}
                    />
                </FormGroup>
            </Box>
        </DHDialog>
    );
};

export default CreateApiClientDialog;
